import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Navigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'ncoded-component-library';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  BODY_CONTENT_GENERATE,
  HEADING_TRANSLATIONS,
  SIZE_VALUES,
  TABLE_COLUMNS,
} from './data';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './QuestionAndAnswersSettings.styles.scss';
import './QuestionAndAnswersSettings.styles.responsive.scss';

type QuestionAndAnswersSettingsProps = {
  className?: string;
};

const QuestionAndAnswersSettings: React.FC<QuestionAndAnswersSettingsProps> = (
  props,
) => {
  const { className } = props;

  const { popSuccess } = useContext(PopNotificationsContext);
  const {
    canBuyerAskQuestion,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { isLoading, buySideGroups, setBuySideGroups, updateMember } =
    useContext(MembersContext);

  const {
    _id,
    autoApproveQuestions,
    dataRoomMembers = [],
  } = buySideGroups?.[0] || {};

  const classes = classNames(
    'question-and-answers-settings',
    'white-panel',
    className,
  );

  const { t } = useTranslation();

  const [updateAutoApproveQuestions, loading] = useCallbackAsync(
    async (autoApproveQuestions: boolean) => {
      const {
        data: { buySideGroups },
      } = await dataRoomApi.members.updateBuySideGroup(_id, {
        autoApproveQuestions,
      });

      const { hasQuestionsInReview } =
        buySideGroups?.find((group) => group._id === _id) || {};

      setBuySideGroups(buySideGroups);

      if (!hasQuestionsInReview || !autoApproveQuestions) return;

      if (
        await confirm({
          title: t('approveAllThatAreInReview'),
          content: t('wouldYouLikeToApproveAllQuestions'),
          submitBtnText: t('approveAllQuestions'),
          cancelBtnText: t('keepAsIs'),
        })
      ) {
        dataRoomApi.members.updateBuySideGroupAllQuestionsApprove(_id);
        popSuccess(t('approveAllQuestionsSuccess'));
      }
    },
  );

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: DataRoomMember) =>
            BODY_CONTENT_GENERATE[col]({
              member,
              onChange: (
                memberId: string,
                updated: Partial<DataRoomMember>,
              ) => {
                dataRoomApi.members.updateMember(memberId, updated);
                updateMember({ _id: memberId, ...updated });
              },
            }),
        }),
        {},
      ),
    [updateMember],
  );

  if (!canBuyerAskQuestion)
    return <Navigate to={getDataRoomRoute(dataRoomId)} replace />;

  return (
    <div className={classes}>
      <p className="title">{t('qaSettings')}</p>

      <Checkbox
        checked={autoApproveQuestions}
        onChange={updateAutoApproveQuestions}
        disabled={loading}
        label={t('sendToSellSideWithoutApproval')}
      />

      {!autoApproveQuestions && (
        <p>{t('sendToSellSideWithoutApprovalDescription')}</p>
      )}

      <ReorderResizeTable
        isDragDisabled
        isResizeDisabled
        columns={TABLE_COLUMNS}
        data={dataRoomMembers}
        isDataLoading={isLoading}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        customSizes={SIZE_VALUES}
      />
    </div>
  );
};

export default QuestionAndAnswersSettings;
